import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import App from './App';
import './globals.css';
import './i18n';
import { restoreSession, useAuth } from './shared/lib/auth';
import { core } from './shared/api/client';
import { readBlobError, setExportContext } from './shared/lib/export-context';
import { downloadReport } from './shared/lib/report';
import { NAV } from './app/nav';
import i18n from './i18n';

// exports carry the ISP's name and the user's, are rendered by the ISP's Core in its brand (Netxol Report) and are
// recorded in the ISP's audit trail; the sidebar group of the page is the report's eyebrow
setExportContext({
  company: () => { const u = useAuth.getState().user as any; return u?.tenant?.name ?? u?.tenantName ?? ''; },
  who: () => { const u = useAuth.getState().user as any; return u?.name ?? u?.email ?? ''; },
  log: (e) => core.post('/exports/log', e),
  render: (doc, format) => downloadReport(doc, format).catch(readBlobError),
  section: () => {
    const path = location.pathname; let best: { key: string; label: string; len: number } | null = null;
    for (const g of NAV) for (const i of g.items) for (const to of [i.to, ...(i.tabs ?? []).flatMap((tb) => [tb.to, ...(tb.also ?? [])])]) {
      const hit = to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);
      if (hit && (!best || to.length > best.len)) best = { key: g.key, label: g.label, len: to.length };
    }
    return best ? String(i18n.t(`nav.groups.${best.key}`, { defaultValue: best.label })) : '';
  },
});
if (localStorage.getItem('nx.theme') === 'dark') document.documentElement.classList.add('dark');
if (!location.pathname.startsWith('/portal')) void restoreSession();   // the access token from the HttpOnly refresh cookie (none is kept in web storage)

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 20_000, refetchOnWindowFocus: false, retry: 1 } },
});
// another person or company signed in (sign-out, "Live demo", a sample workspace, a support session): nothing read for the
// previous one may stay on screen — drop every cached answer
useAuth.subscribe((s, prev) => {
  if (s.user?.id !== prev.user?.id || s.user?.tenant?.code !== prev.user?.tenant?.code) queryClient.clear();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <App />
        <Toaster richColors position="top-right" closeButton />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
